export const httpUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

export const formPdfUrls = (formCompletion: {
  unsignedUrl?: string | null;
  unsignedDocument?: { downloadUrl?: string | null } | null;
}) =>
  [
    formCompletion.unsignedDocument?.downloadUrl,
    formCompletion.unsignedUrl,
  ].flatMap((url) => {
    if (typeof url !== 'string') return [];
    const href = httpUrl(url);
    return href ? [href] : [];
  });

export const loadPdfBytes = async (urls: string[]) => {
  let fallbackUrl: string | null = null;

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.byteLength === 0) continue;
      return { bytes, fallbackUrl: null };
    } catch {
      fallbackUrl = fallbackUrl ?? url;
    }
  }

  if (!fallbackUrl) return null;
  return { bytes: null, fallbackUrl };
};

export type FormPdfDownloadResult =
  | { status: 'noFile' }
  | { status: 'failed' }
  | { status: 'zip'; name: string; bytes: Uint8Array }
  | { status: 'link'; url: string };

export const collectFormDownloads = (results: FormPdfDownloadResult[]) => {
  const zipEntries = results.flatMap((result) =>
    result.status === 'zip' ? [{ name: result.name, bytes: result.bytes }] : [],
  );
  const linkUrls = results.flatMap((result) =>
    result.status === 'link' ? [result.url] : [],
  );
  const attempted = results.filter(
    (result) => result.status !== 'noFile',
  ).length;

  return { zipEntries, linkUrls, attempted };
};

// Signed S3 URLs often omit Access-Control-Allow-Origin, so fetch cannot read
// the body. The URL itself is still a download (Content-Disposition: attachment).
export const downloadSignedFile = (url: string) => {
  const href = httpUrl(url);
  if (!href) return false;

  const iframe = document.createElement('iframe');
  iframe.hidden = true;
  iframe.src = href;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 60_000);
  return true;
};
